import React from 'react';
import { useRsi } from '../../../hooks/useRsi';
import { MatchIcon } from './MatchIcon';
import type { Fields, SelectOption } from '../../../types';
import { MatchColumnSelect } from '../../../components/Selects/MatchColumnSelect';
import { SubMatchingSelect } from './SubMatchingSelect';
import { Box, Grid, styled, Typography, Accordion } from '@beeline.com/bp-design-system';
import { Column, ColumnType } from '../types';
import { RsiTranslations } from '../../../translations/RsiTranslations.types';

const getAccordionTitle = <T extends string>(fields: Fields<T>, column: Column<T>, translations: RsiTranslations) => {
  const fieldLabel = fields.find((field) => 'value' in column && field.key === column.value)!.label;
  return `${translations.matchColumnsStep.matchDropdownTitle} ${fieldLabel} (${
    'matchedOptions' in column && column.matchedOptions.filter((option) => !option.value).length
  } ${translations.matchColumnsStep.unmatched})`;
};

type TemplateColumnProps<T extends string> = {
  onChange: (val: T, index: number) => void;
  onSubChange: (val: T, index: number, option: string) => void;
  column: Column<T>;
  columns: Column<T>[];
};

export const TemplateColumn = <T extends string>({
  column,
  columns,
  onChange,
  onSubChange
}: TemplateColumnProps<T>) => {
  const { translations, fields } = useRsi<T>();
  const isIgnored = column.type === ColumnType.ignored;
  const isChecked =
    column.type === ColumnType.matched ||
    column.type === ColumnType.matchedCheckbox ||
    column.type === ColumnType.matchedSelectOptions;
  const isSelect = 'matchedOptions' in column;

  // Derive keys already selected by other columns, excluding the current column.
  const selectedKeys = columns
    .filter(
      (col: Column<T>): col is Extract<Column<T>, { value: T }> =>
        'value' in col && col.value && col.index !== column.index
    )
    .map((col) => col.value);
  const selectOptions = fields
    .filter(({ key }) => !selectedKeys.includes(key as T)) // Exclude keys already selected by other columns.
    .map(({ label, key }) => ({ value: key, label }) as SelectOption);

  const selectValue = selectOptions.find(({ value }) => 'value' in column && column.value === value);

  return (
    <Grid container minHeight={10} width="100%" direction="column" justifyContent="center">
      {isIgnored ? (
        <Typography pt={3} style={{ opacity: 0.4 }}>
          {translations.matchColumnsStep.ignoredColumnText}
        </Typography>
      ) : (
        <>
          <Box position="relative">
            <div>
              <StyledMatchColumnSelect
                clearable
                name={column.header}
                onChange={(value) => onChange(value?.value as T, column.index)}
                options={selectOptions}
                placeholder={`${column.header}: ${translations.matchColumnsStep.selectPlaceholder}`}
                value={selectValue}
              />
            </div>
            <StyledMatchIcon isChecked={isChecked} />
          </Box>
          {isSelect && (
            <Box pt={3} width="100%">
              <Accordion variant="outlined">
                <Accordion.Summary dataTest={`accordion-${column.header}-button`}>
                  <Box textAlign="left">
                    <Typography>{getAccordionTitle<T>(fields, column, translations)}</Typography>
                  </Box>
                </Accordion.Summary>
                <Accordion.Details>
                  <Box pb={4} pt={2} pr={3} display="flex" flexDirection="column">
                    {column.matchedOptions.map((option) => (
                      <SubMatchingSelect option={option} column={column} onSubChange={onSubChange} key={option.entry} />
                    ))}
                  </Box>
                </Accordion.Details>
              </Accordion>
            </Box>
          )}
        </>
      )}
    </Grid>
  );
};

const StyledMatchIcon = styled(MatchIcon)`
  position: absolute;
  right: 21px;
  top: 10px;
  pointer-events: none;
`;

const StyledMatchColumnSelect = styled(MatchColumnSelect)`
  [aria-label="Clear"] {
    margin-right: 18px;
    top: 1px;
  }
`;
